import React from 'react'

import {isBrowser} from '@/utils/browser'

import {localStorage, sessionStorage} from '../utils/storage'

type SetValueWithConsent<T> = (
    value: React.SetStateAction<T>,
    requiredConsent: keyof Hubspot.ConsentCategories,
) => void

/**
 *
 * @param key The key of the data to be stored in the storage
 * @param initialValue optional initial value to assign storage
 * @param storage: Storage | undefined: The storage is undefined when it is executed on the server
 * @desc: This hook abstracts the sessionStorage and the localStorage API to
 * access from and set data to sessionStorage and localStorage
 */
const useStorage = <T,>(
    key: string,
    initialValue: T,
    storageType: 'local' | 'session',
): [T, SetValueWithConsent<T>] => {
    const [state, setState] = React.useState<T>(initialValue)
    const storage = storageType === 'local' ? localStorage : sessionStorage

    // Get from storage then parse stored json or return initialValue
    const readValue = React.useCallback((): T => {
        if (!isBrowser) {
            return initialValue
        }

        try {
            const item = storage.getItem(key)
            if (!item) {
                return initialValue
            }
            return JSON.parse(item) || initialValue
        } catch (error) {
            console.warn(`Error reading ${storage} key “${key}”:`, error)
            return initialValue
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally leaving out initialValue, we use value at first render
    }, [key, storage])

    // Effect to fetch and store value at first render
    React.useEffect(() => {
        const value = readValue()
        setState(value)
    }, [readValue])

    const setValue: SetValueWithConsent<T> = (value, requiredConsent) => {
        if (!isBrowser) {
            return
        }

        try {
            const valueToStore = value instanceof Function ? value(state) : value
            storage.setItem(key, JSON.stringify(valueToStore), requiredConsent)
            setState(valueToStore)
        } catch (error) {
            console.warn(`Error setting key “${key}”:`, error)
        }
    }

    return [state, setValue]
}

export const useLocalStorage = <T,>(key: string, initialValue: T) =>
    useStorage<T>(key, initialValue, 'local')

export const useSessionStorage = <T,>(key: string, initialValue: T) =>
    useStorage<T>(key, initialValue, 'session')
